/**
 * In-flight replace-selection paint shared by canvas left-click and right-click.
 * The two handlers are separate hook instances, so this cannot live in React state.
 *
 * When a later click aborts an earlier one, it must treat this snapshot as the
 * baseline. Otherwise the optimistic mesh looks "already selected" and skips
 * First/Last resolve.
 */

export type OptimisticPaintSnapshot = {
    ownerGeneration: number;
    previousMainObject: number | undefined;
    previousMainObjects: number[];
    previousHighlightIds: number[];
};

let generation = 0;
let pendingOptimisticPaint: OptimisticPaintSnapshot | null = null;

/** Bump so an aborted click cannot restore a newer click's paint. */
export function beginOptimisticPaintAttempt(): number {
    generation += 1;
    return generation;
}

export function getPendingOptimisticPaint(): OptimisticPaintSnapshot | null {
    return pendingOptimisticPaint;
}

export function setPendingOptimisticPaint(snapshot: OptimisticPaintSnapshot): void {
    pendingOptimisticPaint = snapshot;
}

/** Drop an in-flight optimistic paint so a later abort cannot restore it. */
export function discardPendingOptimisticPaint(): void {
    pendingOptimisticPaint = null;
}

export function isCurrentOptimisticPaintOwner(ownerGeneration: number): boolean {
    return (
        pendingOptimisticPaint !== null &&
        pendingOptimisticPaint.ownerGeneration === ownerGeneration &&
        ownerGeneration === generation
    );
}

/** Test-only: drop leftover generation / paint snapshot from a previous case. */
export function resetOptimisticPaintState(): void {
    generation = 0;
    pendingOptimisticPaint = null;
}
